/* eslint-disable max-lines */
import { createSlice } from '@reduxjs/toolkit';
import { assign, find } from 'lodash';
import { persistReducer } from 'redux-persist';
import sessionStorage from 'redux-persist/lib/storage/session';

import { ShipmentStatus } from 'enums/common.enum';
import lineItemEnum from 'enums/lineItem.enum';
import RequestStatus from 'enums/request.enum';
import WorkQueues, { CENTRAL_FILL_MODE } from 'enums/workQueue.enum';

// Default Bulk Queue filter for Fulfillment and Transfer modes. Central-fill and
// ship-later items are excluded; only unprinted product-fulfillment items show.
// Shared between initialState and the mode switch so the two never drift.
export const DEFAULT_BULK_LABELS_FILTER = {
  patientName: undefined,
  medicationName: undefined,
  tags: undefined,
  tagsNotIn: ['CENTRAL_FILL_RX', 'SHIP_LATER'],
  lastPrintedDate: undefined,
  shipByDate: undefined,
  isPrinted: false,
  taggedForReview: false,
  lineItemStatus: lineItemEnum.PRODUCT_FULFILLMENT,
  fillType: undefined,
  createdAt: undefined,
  itemCount: undefined,
  selectedDrugProfiles: [],
};

export const initialState = {
  preset: '',
  previousPreset: '',
  showLimitedTabs: undefined,
  patientQualification: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      status: 'ONBOARDED',
      gender: undefined,
      createdAfter: undefined,
      dob: undefined,
      name: undefined,
    },
    sortBy: {
      sortKey: 'onBoardedDate',
      sortOrder: 'ASC',
    },
  },
  inClaimEligibility: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      patientName: undefined,
      partnerName: undefined,
      requestedDate: undefined,
      shipByDate: undefined,
      requestStatus: RequestStatus.OPEN,
    },
    sortBy: {
      sortKey: 'shipByDate',
      sortOrder: 'ASC',
    },
  },
  reviewInClaimEligibility: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      patientName: undefined,
      partnerName: undefined,
      requestedDate: undefined,
      shipByDate: undefined,
      taggedForReview: true,
    },
    sortBy: {
      sortKey: 'shipByDate',
      sortOrder: 'ASC',
    },
  },
  orderLineItems: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      patientName: undefined,
      partnerName: undefined,
      requestedDate: undefined,
      shipByDate: undefined,
      orderStatus: undefined,
      fillType: undefined,
      createdAt: undefined,
    },
    sortBy: {
      sortKey: 'shipByDate',
      sortOrder: 'ASC',
    },
  },
  bulkLabels: {
    currentPage: 1,
    pages: {},
    count: 0,
    loading: false,
    bulkMode: 'Fulfillment',
    filter: { ...DEFAULT_BULK_LABELS_FILTER },
    // POM-5788: the filter each mode was last left with, restored on return to that mode.
    filtersByMode: {},
    sortBy: {
      sortKey: 'shipByDate',
      sortOrder: 'ASC',
    },
    bulkLabelHistory: {
      currentPage: 1,
      pages: {},
      count: 0,
      filter: {
        createdBetween: undefined,
        pomUserName: undefined,
      },
      sortBy: {
        sortKey: 'createdAt',
        sortOrder: 'DESC',
      },
    },
  },
  productFulfillment: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      orderId: undefined,
      patientName: undefined,
      mpi: undefined,
      shipByDate: undefined,
    },
    sortBy: {
      sortKey: 'shipByDate',
      sortOrder: 'ASC',
    },
  },
  orderVerification: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      patientName: undefined,
      shipByDate: undefined,
      orderId: undefined,
      orderStatus: ['ORDER_VERIFICATION', 'ORDER_RE_VERIFICATION'],
      createdAt: undefined,
    },
    sortBy: {
      sortKey: 'shipByDate',
      sortOrder: 'ASC',
    },
  },
  readyToShip: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      flatSearch: undefined,
      orderId: undefined,
      recipientName: undefined,
      trackingNumber: undefined,
      shipmentStatus: [ShipmentStatus.REQUEST_CREATED],
    },
    sortBy: {
      sortKey: 'createdAt',
      sortOrder: 'ASC',
    },
  },
  reviewAdjudication: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      patientName: undefined,
      adjudicatedTime: undefined,
      qs1Id: undefined,
      rxNumber: undefined,
      adjudicationStatus: undefined,
    },
    sortBy: {
      sortKey: 'adjudicatedTime',
      sortOrder: 'ASC',
    },
  },
  profileIncomplete: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      name: undefined,
      dob: undefined,
      createdAfter: undefined,
    },
    sortBy: {
      sortKey: 'createdTimestamp',
      sortOrder: 'ASC',
    },
  },
  prescriptionReview: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      drugName: undefined,
      requestedDate: undefined,
      reviewStatus: undefined,
      fillType: undefined,
      tags: undefined,
      tagsNotIn: undefined,
    },
    sortBy: {
      sortKey: 'requestedDate',
      sortOrder: 'ASC',
    },
  },
  eVisitRequests: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      bundleLabel: undefined,
      requestedDate: undefined,
      requestId: undefined,
      requestReferenceId: undefined,
    },
    sortBy: {
      sortKey: 'requestedDate',
      sortOrder: 'ASC',
    },
  },
  rxIntakeRequests: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      patientName: undefined,
      medicationName: undefined,
      requestStatus: 'OPEN',
      practiceName: undefined,
      scriptId: undefined,
      createdAt: undefined,
    },
    sortBy: {
      sortKey: 'createdAt',
      sortOrder: 'ASC',
    },
  },
  processedExternalOrders: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      patientName: undefined,
      status: undefined,
      createdAt: undefined,
      partnerName: undefined,
      partnerAccount: undefined,
      orderId: undefined,
    },
    sortBy: {
      sortKey: 'createdAt',
      sortOrder: 'DESC',
    },
  },
  compoundingQueue: {
    currentPage: 1,
    pages: {},
    count: 0,
    // POM-6099: which view of the Central Fill Rx tab is on screen. Relay is the admin-only
    // minority view, so the default is the one most operators are here for.
    mode: CENTRAL_FILL_MODE.CENTRAL_FILL,
    filter: {
      orderId: undefined,
      statuses: undefined,
      partnerIds: undefined,
      compoundingPharmacyIds: undefined,
      drugNameContains: undefined,
      submittedAfter: undefined,
      submittedBefore: undefined,
    },
    sortBy: {
      sortKey: 'lastUpdatedAt',
      sortOrder: 'DESC',
    },
  },
  /**
   * POM-6099 relay orders. A SEPARATE slice of state from compoundingQueue even though the two
   * share a tab, because the generic page/count/sort reducers key on `state[preset]` and the
   * active `preset` stays 'compoundingQueue' while relay is on screen. Relay reads therefore pass
   * `preset: 'relayQueue'` EXPLICITLY; letting it default would write relay orders into the
   * central fill queue's pages, and the screen would render one queue's rows under the other's
   * columns with no error anywhere.
   */
  relayQueue: {
    currentPage: 1,
    pages: {},
    count: 0,
    // POM-6279: core's default order.
    sortBy: { sortKey: 'transferredAt', sortOrder: 'DESC' },
    /*
     * EXACTLY the keys the filter UI offers: three substring matches, `deliveryMethods` and
     * `statuses` (POM-6279: TRANSFERRED by default; cleared, every queue status shows).
     *
     * `updateFilter` REPLACES this object rather than merging into it, so every caller of
     * updateRelayQueueFilter must send the whole filter or it clears the keys it left out.
     */
    filter: {
      patientNameContains: undefined,
      partnerNameContains: undefined,
      drugNameContains: undefined,
      deliveryMethods: undefined,
      statuses: ['TRANSFERRED'],
    },
  },
  // Fill Later work queue (POM-6149). Default view = actionable rows + failures, nearest date first.
  fillLater: {
    currentPage: 1,
    pages: {},
    count: 0,
    loading: false,
    filter: {
      statuses: ['SCHEDULED', 'WAITING_SHIPMENT', 'FAILED'],
    },
    sortBy: {
      sortKey: 'scheduledFor',
      sortOrder: 'ASC',
    },
  },
  // Non-Routable Orders holding queue (POM-6223). EVERY ROW HERE IS A PRESCRIPTION THAT IS NOT
  // BEING FILLED, and this screen is the only place it is visible.
  //
  // The filter starts EMPTY on purpose. Every other queue ships a default status filter; this one
  // must not, because a default that hides rows makes a backlog look like an empty queue, and an
  // empty-looking queue reads as "nothing is stuck" — the most dangerous state this screen has.
  // The backend already restricts an UNFILTERED read to PENDING | RESOLUTION_IN_PROGRESS — its
  // default list, which is narrower than what the filter accepts (CANCELLED is filterable). That
  // is why shipping `filter: {}` is still the right default and does not hide a backlog.
  nonRoutableRequests: {
    currentPage: 1,
    pages: {},
    count: 0,
    loading: false,
    filter: {},
    // parkedAt DESC is the only index-native ordering, and WHICH index serves it depends on the
    // status filter: idx_non_routable_request_queue for this default, idx_non_routable_request_
    // cancelled for a Cancelled-only filter, and neither for a mixed selection.
    sortBy: {
      sortKey: 'parkedAt',
      sortOrder: 'DESC',
    },
  },
  reconcileShipment: {
    currentPage: 1,
    pages: {},
    count: 0,
    filter: {
      spOrderId: undefined,
      orderNumber: undefined,
      trackingNumber: undefined,
      patientName: undefined,
    },
    sortBy: {
      sortKey: 'createdAt',
      sortOrder: 'DESC',
    },
    deleteIrreconcileOrderItems: [],
    selectedReconcileId: null,
    searchInputValue: '',
    suggestionsLoading: false,
    linkOrderLoading: false,
    deleteOrderLoading: false,
    filteredData: [],
    selectedShipstationOrderDetails: null,
    ordersSuggestionsCurrentPage: 1,
    ordersSuggestionsCount: 0,
    ordersSuggestionsPages: {},
  },
  // Tracks in-flight label generation per shipment so state persists across dialog close/reopen
  labelGeneration: {},
  lockedInventoryItems: [],
  // POM-6033: lock changes that landed while a full re-read was in flight. That read's snapshot
  // predates them, so it is replayed over instead of trusted — otherwise a reconnect resync
  // silently reinstates a lock that was released a moment earlier, which is the very bug this
  // feature removes.
  lockedInventoryPendingDeltas: [],
  lockedInventorySyncing: false,
  // POM-6033: the last socket push, and a counter the UI watches. Reads are deliberately not
  // recorded — a read cannot say when a lock happened, only that it holds.
  lockedInventoryLastDelta: null,
  lockedInventoryDeltaSeq: 0,
};

/**
 * Merges one physical-count lock change into the locked-item list (POM-6033).
 *
 * Shared by the live delta and by the replay that runs after a full re-read, so a replayed
 * change lands exactly as the original did.
 *
 * Only the two known actions do anything. An unrecognised one is ignored rather than falling
 * through to a release: a backend rename must not silently unlock rows in the UI and invite the
 * user to fill a drug that is still under count.
 */
const applyPhysicalCountDelta = (state, { changeType, items }) => {
  // What the list actually gained or lost, so a no-op change announces nothing. Also recovers a
  // released drug's name, which the UNLOCKED payload does not carry.
  const resolved = [];

  if (!items?.length) return resolved;

  items.forEach((item) => {
    const { ndc, fulfillingPharmacyId } = item;
    if (!ndc || !fulfillingPharmacyId) return;

    const index = state.lockedInventoryItems.findIndex(
      (existing) => existing.ndc === ndc && existing.fulfillingPharmacyId === fulfillingPharmacyId
    );

    if (changeType === 'LOCKED') {
      const entry = {
        ndc,
        fulfillingPharmacyId,
        drugName: item.drugName ?? '',
        strength: item.strength ?? '',
        productId: item.productId,
        // POM-6112 — the banner dialog now shows manufacturer to separate sibling NDCs of one
        // product. This entry is a whitelist, so a field left out here reads as blank on a
        // live-locked row while the same row is populated after the next full re-read.
        manufacturer: item.manufacturer ?? '',
        // POM-6112 — whether THIS ndc is the literal lock or only a sibling shown for context.
        // Same whitelist trap as manufacturer, with a worse symptom: dropped here, every chip
        // from a live lock renders as "not itself locked", the locked one included. The entry
        // also replaces a listed row wholesale, so omitting it would wipe a flag the full read
        // had right.
        isLocked: Boolean(item.isLocked),
      };

      if (index === -1) {
        state.lockedInventoryItems.push(entry);
        resolved.push(entry);
      } else {
        state.lockedInventoryItems[index] = entry;
      }
    } else if (changeType === 'UNLOCKED' && index !== -1) {
      const released = state.lockedInventoryItems[index];
      resolved.push({ ...released });

      // POM-6112 — releasing one NDC of a product that ANOTHER count still holds does not take
      // it off the banner: a refetch would list it as a sibling of the surviving lock. The
      // backend names only the released pair in that case, so downgrade it instead of dropping
      // it, or it would vanish live and reappear on reload.
      //
      // Anchors exclude everything this same push releases, so unlocking two NDCs of one
      // product together still clears both.
      const stillAnchored =
        Boolean(released.productId) &&
        state.lockedInventoryItems.some(
          (other) =>
            other.isLocked &&
            other.productId === released.productId &&
            other.fulfillingPharmacyId === released.fulfillingPharmacyId &&
            !items.some(
              (releasedItem) =>
                releasedItem.ndc === other.ndc &&
                releasedItem.fulfillingPharmacyId === other.fulfillingPharmacyId
            )
        );

      if (stillAnchored) {
        state.lockedInventoryItems[index] = { ...released, isLocked: false };
      } else {
        state.lockedInventoryItems.splice(index, 1);
      }
    }
  });

  return resolved;
};

/* eslint no-param-reassign: ["error", { "ignorePropertyModificationsFor": ["state"] }] */
const workQueueSlice = createSlice({
  name: 'workQueue',
  initialState,
  reducers: {
    loadSelectedWorkQueues(state, action) {
      if (state.preset && state.preset !== action.payload) {
        state.previousPreset = state.preset;
      }
      state.preset = action.payload;
    },
    updateCentralFillQueueMode(state, action) {
      state.compoundingQueue.mode = action.payload.mode;
    },
    /**
     * POM-6099. Drops relayed-order rows and returns the Central Fill Rx tab to its default view.
     *
     * Called from resetAppState on sign-out. relayQueue holds SYS_ADMIN-only rows (patient MPI,
     * drug name, NDC) and the mode can still say RELAY, so without this the next person to sign
     * in on the same tab is shown the previous sys-admin's relayed orders until a refetch lands.
     */
    resetRelayQueueState(state) {
      state.relayQueue = initialState.relayQueue;
      state.compoundingQueue.mode = initialState.compoundingQueue.mode;
    },
    loadWorkQueueToPage(state, action) {
      const { preset, page, items, count } = action.payload;

      state[preset].count = count;
      state[preset].pages[page] = items;
    },
    updateCurrentPage(state, action) {
      const { preset, page } = action.payload;

      state[preset].currentPage = page;
    },
    updateSortBy(state, action) {
      const { preset, sortBy } = action.payload;

      state[preset].pages = {};
      state[preset].count = 0;
      state[preset].sortBy = sortBy;
    },
    updateFilter(state, action) {
      const { preset, filter } = action.payload;

      state[preset].pages = {};
      state[preset].count = 0;
      state[preset].filter = filter;
    },
    updateLineItemShipByDate(state, action) {
      const { lineItemId, shipByDate, tags } = action.payload;

      const orderLineItemsPage = state.orderLineItems.pages[state.orderLineItems.currentPage];
      if (orderLineItemsPage) {
        const item = find(orderLineItemsPage, { lineItemId });
        if (item) assign(item, { shipByDate, tags });
      }

      const bulkLabelsPage = state.bulkLabels.pages[state.bulkLabels.currentPage];
      if (bulkLabelsPage) {
        const item = find(bulkLabelsPage, { lineItemId });
        if (item) assign(item, { shipByDate, tags });
      }

      // Update orderVerification — items store lineItemId directly
      const orderVerificationPage =
        state.orderVerification.pages[state.orderVerification.currentPage];
      if (orderVerificationPage) {
        const item = find(orderVerificationPage, { lineItemId });
        if (item) assign(item, { shipByDate, tags });
      }

      // Update prescriptionReview — items nest tags/shipByDate under .lineItem
      const prescriptionReviewPage =
        state.prescriptionReview.pages[state.prescriptionReview.currentPage];
      if (prescriptionReviewPage) {
        const item = find(
          prescriptionReviewPage,
          (prescription) => prescription.lineItem?.requestLineItemId === lineItemId
        );
        if (item?.lineItem) assign(item.lineItem, { shipByDate, tags });
      }
    },
    loadFailedAdjudicationNotes(state, action) {
      const { notes, contextId, hasPinnedNotes } = action.payload;

      const pageIndex = state.reviewAdjudication.currentPage;
      const failedPrescription = find(state.reviewAdjudication.pages[pageIndex], {
        prescriptionId: contextId,
      });
      assign(failedPrescription, { notes, hasPinnedNotes });
    },
    loadReviewPrescriptionNotes(state, action) {
      const { notes, contextId } = action.payload;
      const pageIndex = state.prescriptionReview.currentPage;
      const reviewPrescription = find(state.prescriptionReview.pages[pageIndex], {
        pomPrescriptionId: contextId,
      });

      assign(reviewPrescription, { notes });
    },
    setShowLimitedTabs(state, action) {
      const { showLimitedTabs } = action.payload;
      state.showLimitedTabs = showLimitedTabs;
    },
    toggleIrreconcileShipmentSelection(state, action) {
      const spOrderId = action.payload;
      const selectedItems = state.reconcileShipment.deleteIrreconcileOrderItems;
      const index = selectedItems?.indexOf(spOrderId);
      if (index === -1) {
        selectedItems?.push(spOrderId);
      } else {
        selectedItems?.splice(index, 1);
      }
    },
    clearSelectedIrreconcileOrders: (state) => {
      state.reconcileShipment.deleteIrreconcileOrderItems = [];
    },
    setReconcileSelectedReconcileId: (state, action) => {
      state.reconcileShipment.selectedReconcileId = action.payload;
    },
    setReconcileSearchInput: (state, action) => {
      state.reconcileShipment.searchInputValue = action.payload;
    },
    clearReconcileFilterControls: (state) => {
      state.reconcileShipment.selectedReconcileId = null;
    },
    setSuggestionsLoading(state, action) {
      state.reconcileShipment.suggestionsLoading = action.payload;
    },
    setOrderLinkLoading(state, action) {
      state.reconcileShipment.linkOrderLoading = action.payload;
    },
    setOrderDeleteLoading(state, action) {
      state.reconcileShipment.deleteOrderLoading = action.payload;
    },
    setFilteredData: (state, action) => {
      state.reconcileShipment.filteredData = action.payload;
    },
    setSelectedShipstationOrderDetails: (state, action) => {
      state.reconcileShipment.selectedShipstationOrderDetails = action.payload;
    },
    loadOrdersSuggestionsToPage(state, action) {
      const { page, items, count } = action.payload;
      state.reconcileShipment.ordersSuggestionsCount = count;
      state.reconcileShipment.ordersSuggestionsPages[page] = items;
    },
    updateOrdersSuggestionsPage(state, action) {
      const { page } = action.payload;
      state.reconcileShipment.ordersSuggestionsCurrentPage = page;
    },
    clearOrdersSuggestionsPages(state) {
      state.reconcileShipment.ordersSuggestionsPages = {};
      state.reconcileShipment.ordersSuggestionsCount = 0;
      state.reconcileShipment.ordersSuggestionsCurrentPage = 1;
    },
    setWorkQueueLoading(state, action) {
      const { preset, loading } = action.payload;
      state[preset].loading = loading;
    },
    setUpdateFilterOnHistory(state, action) {
      const { preset, filter } = action.payload;

      state[preset].bulkLabelHistory.pages = {};
      state[preset].bulkLabelHistory.count = 0;
      state[preset].bulkLabelHistory.filter = filter;
    },
    setBulkHistoryViewLoading(state, action) {
      const { preset, loading } = action.payload;
      state[preset].bulkLabelHistory.loading = loading;
    },
    updateBulkLabelHistorySortBy(state, action) {
      const { preset, sortBy } = action.payload;

      state[preset].bulkLabelHistory.pages = {};
      state[preset].bulkLabelHistory.count = 0;
      state[preset].bulkLabelHistory.sortBy = sortBy;
    },
    loadBulkHistoryToPage(state, action) {
      const { preset, page, items, count } = action.payload;

      state[preset].bulkLabelHistory.count = count;
      state[preset].bulkLabelHistory.pages[page] = items;
    },
    updateBulkHistoryPage(state, action) {
      const { preset, page } = action.payload;

      state[preset].bulkLabelHistory.currentPage = page;
    },
    updateLineItemTags(state, action) {
      const { preset, page, lineItemId, tags } = action.payload;
      const isRegenerating =
        Array.isArray(tags) && tags.some((tag) => tag.tagName === 'LABEL_REGENERATING');

      if (preset === WorkQueues.PRESCRIPTION_REVIEW) {
        const prescriptionReviewItems = state[preset].pages[page] || [];
        const item = find(prescriptionReviewItems, (prescription) => {
          return prescription.lineItem?.requestLineItemId === lineItemId;
        });
        if (item) {
          assign(item.lineItem, {
            tags,
            labelRegenerationStatus: isRegenerating ? 'REGENERATING' : null,
          });
        }
      } else if (preset === WorkQueues.BULK_LABELS) {
        const bulkLabelsItems = state[preset].pages[page] || [];
        const item = find(bulkLabelsItems, (bulkLabel) => {
          return bulkLabel.lineItemId === lineItemId;
        });
        if (item) {
          assign(item, { tags, labelRegenerationStatus: isRegenerating ? 'REGENERATING' : null });
        }
      } else if (preset === WorkQueues.ORDER_VERIFICATION) {
        const orderVerificationItems = state[preset].pages[page] || [];
        const item = find(orderVerificationItems, (lineItem) => lineItem.lineItemId === lineItemId);
        if (item) {
          assign(item, { tags });
        }
      }
    },
    updateBulkLabelsMode(state, action) {
      const { bulkMode } = action.payload;
      const { bulkLabels } = state;

      if (bulkLabels.bulkMode !== bulkMode) {
        bulkLabels.filtersByMode[bulkLabels.bulkMode] = { ...bulkLabels.filter };
      }
      bulkLabels.bulkMode = bulkMode;
    },
    loadPrescriptionData(state, action) {
      const {
        prescriptionId,
        prescriptionData,
        existingPrescriptions,
        status,
        directionReview,
        parentLineItemQuantity,
      } = action.payload;
      const pageIndex = state.prescriptionReview.currentPage;
      const reviewPrescription = find(state.prescriptionReview.pages[pageIndex], {
        pomPrescriptionId: prescriptionId,
      });

      if (reviewPrescription) {
        assign(reviewPrescription, {
          prescriptionData,
          existingPrescriptions,
          status,
          directionReview,
          parentLineItemQuantity,
          prescriptionDataLoaded: true,
        });
      }
    },
    setSelectedDrugProfiles(state, action) {
      const { drugProfiles } = action.payload;
      state.bulkLabels.filter.selectedDrugProfiles = drugProfiles;
    },
    clearDrugProfiles(state) {
      state.bulkLabels.filter.selectedDrugProfiles = [];
    },
    labelGenerationStarted(state, action) {
      const { shipmentId } = action.payload;
      state.labelGeneration[shipmentId] = { isCreating: true, progress: '' };
    },
    labelProgressUpdated(state, action) {
      const { shipmentId, progress } = action.payload;
      if (state.labelGeneration[shipmentId]) {
        state.labelGeneration[shipmentId].progress = progress;
      }
    },
    labelGenerationFinished(state, action) {
      const { shipmentId } = action.payload;
      delete state.labelGeneration[shipmentId];
    },
    /**
     * A full re-read of the locked list has been issued. Anything that arrives before it resolves
     * is buffered, because the response was computed before those changes happened.
     */
    physicalCountSyncStarted(state) {
      state.lockedInventorySyncing = true;
      state.lockedInventoryPendingDeltas = [];
    },
    physicalCountSyncFailed(state) {
      state.lockedInventorySyncing = false;
      state.lockedInventoryPendingDeltas = [];
    },
    setLockedInventoryItems(state, action) {
      state.lockedInventoryItems = action.payload;

      // Re-apply what landed while this read was in flight, or the stale snapshot wins. Leaves the
      // delta counter alone — each was already announced when it first arrived.
      state.lockedInventoryPendingDeltas.forEach((delta) => applyPhysicalCountDelta(state, delta));

      state.lockedInventoryPendingDeltas = [];
      state.lockedInventorySyncing = false;
    },
    /** The only path that records an announceable change — see `lockedInventoryLastDelta`. */
    applyPhysicalCountLockChange(state, action) {
      const resolved = applyPhysicalCountDelta(state, action.payload);

      if (resolved.length) {
        state.lockedInventoryLastDelta = { changeType: action.payload.changeType, items: resolved };
        state.lockedInventoryDeltaSeq += 1;
      }

      if (state.lockedInventorySyncing) {
        state.lockedInventoryPendingDeltas.push(action.payload);
      }
    },
  },
});

const localPersistConfig = {
  key: 'workQueue',
  storage: sessionStorage,
  whitelist: ['preset', 'previousPreset', 'showLimitedTabs'],
};

export const workQueueActions = workQueueSlice.actions;
export default persistReducer(localPersistConfig, workQueueSlice.reducer);
